import {
  ElementNode,
  type LexicalNode,
  type NodeKey,
  type SerializedElementNode,
  type Spread,
} from 'lexical';

import type { NoteEditorTextTone } from '../../config';

import { markBlockDom, markToneDom } from './blockDom';

export type FreeTextLook = {
  placeholder: string;
  tone: NoteEditorTextTone;
  hint: string;
  spaceBefore: boolean;
};

type SerializedNoteFreeTextNode = Spread<
  { blockId: string } & FreeTextLook,
  SerializedElementNode
>;

const markFreeTextDom = (
  dom: HTMLElement,
  blockId: string,
  look: FreeTextLook,
) => {
  markBlockDom(dom, blockId, look.spaceBefore);
  markToneDom(dom, look.tone);
  dom.dataset.placeholder = look.placeholder;
  if (look.hint) {
    dom.title = look.hint;
  } else {
    dom.removeAttribute('title');
  }
  return dom;
};

// A free-text block: its value is text of the document inside an inline
// element that keeps the block's id, and stays when the text is cleared.
export class NoteFreeTextNode extends ElementNode {
  __blockId: string;
  __look: FreeTextLook;

  static getType(): string {
    return 'note-free-text';
  }

  static clone(node: NoteFreeTextNode): NoteFreeTextNode {
    return new NoteFreeTextNode(node.__blockId, node.__look, node.__key);
  }

  static importJSON(json: SerializedNoteFreeTextNode): NoteFreeTextNode {
    const { blockId, placeholder, tone, hint, spaceBefore } = json;
    return $createNoteFreeTextNode({
      id: blockId,
      look: { placeholder, tone, hint, spaceBefore },
    }).updateFromJSON(json);
  }

  constructor(
    blockId = '',
    look: FreeTextLook = {
      placeholder: '',
      tone: null,
      hint: '',
      spaceBefore: false,
    },
    key?: NodeKey,
  ) {
    super(key);
    this.__blockId = blockId;
    this.__look = look;
  }

  exportJSON(): SerializedNoteFreeTextNode {
    return {
      ...super.exportJSON(),
      blockId: this.getBlockId(),
      ...this.getLook(),
    };
  }

  createDOM(): HTMLElement {
    return markFreeTextDom(
      document.createElement('span'),
      this.__blockId,
      this.__look,
    );
  }

  updateDOM(previous: NoteFreeTextNode, dom: HTMLElement): boolean {
    if (previous.__look !== this.__look) {
      markFreeTextDom(dom, this.__blockId, this.__look);
    }
    return false;
  }

  isInline(): boolean {
    return true;
  }

  canBeEmpty(): boolean {
    return true;
  }

  getBlockId(): string {
    return this.getLatest().__blockId;
  }

  getLook(): FreeTextLook {
    return this.getLatest().__look;
  }

  setLook(look: FreeTextLook): this {
    const writable = this.getWritable();
    writable.__look = look;
    return writable;
  }
}

export const $createNoteFreeTextNode = ({
  id,
  look,
}: {
  id: string;
  look: FreeTextLook;
}) => new NoteFreeTextNode(id, look);

export const $isNoteFreeTextNode = (
  node: LexicalNode | null | undefined,
): node is NoteFreeTextNode => node instanceof NoteFreeTextNode;
